import { useState, useRef, useEffect } from 'react';
import { Bell, MessageCircle, Check, CheckCheck, Phone, UserPlus, LifeBuoy, MessageSquare, RefreshCw } from 'lucide-react';
import { useNotifications, UserNotification } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getNotificationTarget } from './notificationTarget';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/user.types';

/**
 * NotificationBell - Real-time notification bell with dropdown
 * Shows unread count badge and list of recent notifications
 */
export function NotificationBell() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  // There is no per-user notifications page; the only "all notifications" view
  // is the super-user-only /admin/notifications, so only super users get the link.
  const canViewAllNotifications = userProfile?.role === UserRole.PROPCLOUDAI_SUPER_USER;
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Handle notification click
  const handleNotificationClick = async (notification: UserNotification) => {
    await markAsRead(notification.id);
    setIsOpen(false);

    const target = getNotificationTarget(notification);
    if (target) navigate(target);
  };

  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'whatsapp_reply':
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      case 'call_centre_disposition':
        return <Phone className="w-4 h-4 text-emerald-500" />;
      case 'new_lead_assigned':
        return <UserPlus className="w-4 h-4 text-green-600" />;
      case 'support_new_item':
      case 'support_assigned':
        return <LifeBuoy className="w-4 h-4 text-blue-500" />;
      case 'support_staff_reply':
      case 'support_user_reply':
        return <MessageSquare className="w-4 h-4 text-blue-600" />;
      case 'support_status_change':
        return <RefreshCw className="w-4 h-4 text-neutral-500" />;
      default:
        return <Bell className="w-4 h-4 text-neutral-500" />;
    }
  };

  // Format timestamp
  const formatTime = (notification: UserNotification) => {
    try {
      if (!notification.createdAt) return '';
      const date = notification.createdAt.toDate();
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-neutral-600" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-neutral-200 overflow-hidden"
          style={{ zIndex: 10003 }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-neutral-100 flex justify-between items-center bg-neutral-50">
            <span className="font-semibold text-neutral-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-primary hover:text-primary-dark flex items-center gap-1 transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="inline-block w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-neutral-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-neutral-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    px-4 py-3 border-b border-neutral-100 cursor-pointer
                    hover:bg-neutral-50 transition-colors
                    ${!notification.read ? 'bg-primary/5' : ''}
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon / Unread indicator */}
                    <div className="flex-shrink-0 mt-0.5">
                      {!notification.read ? (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                          {getNotificationIcon(notification.type)}
                        </span>
                      ) : (
                        <span className="flex h-5 w-5 items-center justify-center text-neutral-400">
                          {getNotificationIcon(notification.type)}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!notification.read ? 'font-medium text-neutral-900' : 'text-neutral-700'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-neutral-500 truncate mt-0.5">
                        {notification.body ?? notification.message}
                      </p>
                      <p className="text-xs text-neutral-400 mt-1">
                        {formatTime(notification)}
                      </p>
                    </div>

                    {/* Read indicator */}
                    {notification.read && (
                      <Check className="w-4 h-4 text-neutral-300 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && canViewAllNotifications && (
            <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin/notifications');
                }}
                className="w-full text-center text-xs text-primary hover:text-primary-dark transition-colors py-1"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
